/**
 * Menu rules: time windows, per-channel / time-based prices (F-DEAL-N2).
 *
 * MIRRORED, three ways — change them together:
 *   backend/internal/pricing/menu_rules.go     (the authority: the server charges this)
 *   frontend/src/lib/menuRules.ts               (counter / Server tablet preview)
 *   storefront/src/lib/menuRules.ts             (online ordering preview)
 * Pinned equal by backend/internal/pricing/testdata/menu_rules/cases.json.
 *
 * Clients never send a price (CLAUDE.md invariant #11): this only lets the
 * till and the online store SHOW the price the server will charge.
 */

export const ONLINE_CHANNEL = 'online'

/** The moment rules are evaluated at, in the business's local time. */
export interface MenuClock {
  /** 0 = Sunday … 6 = Saturday */
  weekday: number
  /** minutes since local midnight, 0..1439 */
  minute: number
  /** YYYY-MM-DD (local) */
  date: string
}

/** "These days, between these minutes". days [] = every day; null times = all day. */
export interface TimeWindow {
  days: number[]
  start_minute: number | null
  end_minute: number | null
}

export interface PriceRule extends TimeWindow {
  id: string
  product_id?: string
  label: string
  price: number
  /** [] = every channel; built-ins, custom ot_* types, or 'online'. */
  order_types: string[]
}

export interface ChannelContext {
  orderType: string
  online: boolean
}

export interface PriceChoice {
  price: number
  rule_id: string
  label: string
}

export function roundMoney(v: number): number {
  return Math.round(v * 100) / 100
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

/**
 * Business-local clock from an instant and the business's UTC offset (sent by
 * the server), so a device with the wrong time zone still evaluates windows in
 * restaurant time.
 */
export function clockAt(nowMs: number, utcOffsetMinutes: number): MenuClock {
  const d = new Date(nowMs + utcOffsetMinutes * 60_000)
  return {
    weekday: d.getUTCDay(),
    minute: d.getUTCHours() * 60 + d.getUTCMinutes(),
    date: `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`,
  }
}

function dayIn(days: readonly number[] | null | undefined, d: number): boolean {
  if (!days || days.length === 0) return true
  return days.includes(d)
}

export function hasTime(w: TimeWindow): boolean {
  return w.start_minute != null && w.end_minute != null
}

/**
 * Whether the window is open at clock c. end < start crosses midnight; the part
 * after midnight belongs to the day the window started on.
 */
export function windowMatches(w: TimeWindow, c: MenuClock): boolean {
  if (w.start_minute == null || w.end_minute == null) return dayIn(w.days, c.weekday)
  const s = w.start_minute
  const e = w.end_minute
  if (s < e) return dayIn(w.days, c.weekday) && c.minute >= s && c.minute < e
  if (s > e) {
    if (c.minute >= s && dayIn(w.days, c.weekday)) return true
    return c.minute < e && dayIn(w.days, (c.weekday + 6) % 7)
  }
  return false
}

function channelMatches(types: readonly string[] | null | undefined, ch: ChannelContext): boolean {
  if (!types || types.length === 0) return true
  const ot = ch.orderType.trim().toLowerCase()
  for (const raw of types) {
    const t = raw.trim().toLowerCase()
    if (t !== '' && t === ot) return true
    if (t === ONLINE_CHANNEL && ch.online) return true
  }
  return false
}

/**
 * The product's base price for a channel and moment. Most specific matching
 * rule wins (timed = 2, channel = 1), then the lower price, then the rule id.
 */
export function resolveBasePrice(
  listPrice: number,
  rules: readonly PriceRule[] | null | undefined,
  ch: ChannelContext,
  c: MenuClock,
): PriceChoice {
  let best: PriceChoice = { price: listPrice, rule_id: '', label: '' }
  let bestScore = -1
  const cands = (rules ?? []).filter((r) => channelMatches(r.order_types, ch) && windowMatches(r, c))
  cands.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
  for (const r of cands) {
    let score = 0
    if (hasTime(r)) score += 2
    if (r.order_types && r.order_types.length > 0) score += 1
    if (score > bestScore || (score === bestScore && r.price < best.price)) {
      best = { price: roundMoney(r.price), rule_id: r.id, label: r.label }
      bestScore = score
    }
  }
  return best
}

/** Orderable now: inside one of its windows (if any) AND its category's (if any). */
export function availableNow(
  productWindows: readonly TimeWindow[] | null | undefined,
  categoryWindows: readonly TimeWindow[] | null | undefined,
  c: MenuClock,
): boolean {
  const anyMatch = (ws: readonly TimeWindow[] | null | undefined) =>
    !ws || ws.length === 0 || ws.some((w) => windowMatches(w, c))
  return anyMatch(productWindows) && anyMatch(categoryWindows)
}

/** "12:00 PM" */
export function formatMinute(m: number): string {
  const h = Math.floor(m / 60)
  const mm = m % 60
  const suffix = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${pad2(mm)} ${suffix}`
}

/** "12:00 PM – 4:00 PM, 7:00 PM – 11:00 PM" */
export function describeWindows(ws: readonly TimeWindow[]): string {
  return ws
    .filter(hasTime)
    .map((w) => `${formatMinute(w.start_minute as number)} – ${formatMinute(w.end_minute as number)}`)
    .join(', ')
}

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** "Mon–Fri" style summary of a day list ([] = "Every day"). */
export function describeDays(days: readonly number[]): string {
  if (!days || days.length === 0 || days.length === 7) return 'Every day'
  const sorted = [...days].sort((a, b) => a - b)
  const contiguous = sorted.every((d, i) => i === 0 || d === sorted[i - 1] + 1)
  if (contiguous && sorted.length > 2) return `${DAY_SHORT[sorted[0]]}–${DAY_SHORT[sorted[sorted.length - 1]]}`
  return sorted.map((d) => DAY_SHORT[d]).join(', ')
}
